/**
 * Who is speaking when on a multicam set's timeline, as PithPlay stores it:
 * one label per short frame (100 ms), run-length encoded ("S12A40X5B33").
 *
 * Labels:
 *  - an angle key (A to F): that angle's person is clearly the one talking
 *  - X: several at once (crosstalk, laughter), or no way to tell them apart
 *  - S: silence
 *  - N: no angle has sound here
 *
 * The browser works it out (src/utils/media/speakingActivity.ts) and stores it
 * on the set; the submitted snapshot carries it to the editor. Used here for
 * the transcript's speaker names, and by the suggested cuts.
 *
 * The backend keeps a copy of the format and the span rule in
 * src/services/speakingActivity.ts: keep the two in step.
 */

export const ACTIVITY_VERSION = 1;
export const SILENCE_LABEL = 'S';
export const SEVERAL_LABEL = 'X';
export const NO_SOUND_LABEL = 'N';
/** Longest name a creator can give an angle's person. */
export const MAX_SPEAKER_NAME_LENGTH = 60;

export type ActivityReason = 'one-angle' | 'too-little-speech' | 'too-alike';

/** As the API stores it (MulticamSpeakingActivity). */
export interface SpeakingActivity {
  version: number;
  frameMs: number;
  durationMs: number;
  /** The angles whose sound was compared. */
  angleKeys: string[];
  /** False when PithPlay couldn't tell who's speaking: then nothing is suggested or labelled. */
  confident: boolean;
  reason?: ActivityReason | string | null;
  runs: string;
  /** The angles' timing it was worked out for (see speakingBasis). */
  basis: string;
  computedAt?: string | null;
}

export interface ActivityRun {
  label: string;
  /** First frame. */
  start: number;
  /** Frames. */
  length: number;
}

const RUN_PATTERN = /([ABCDEFSXN])([1-9]\d{0,6})/g;
const WHOLE_PATTERN = /^(?:[ABCDEFSXN][1-9]\d{0,6})+$/;

/** "S12A40X5": adjacent runs of the same label are joined. */
export const encodeRuns = (runs: Array<{ label: string; length: number }>): string => {
  let text = '';
  let label = '';
  let length = 0;
  runs.forEach((run) => {
    if (!(run.length > 0)) return;
    if (run.label === label) {
      length += run.length;
      return;
    }
    if (length > 0) text += `${label}${length}`;
    label = run.label;
    length = run.length;
  });
  if (length > 0) text += `${label}${length}`;
  return text;
};

/** The runs, with their first frames; null when the text isn't a valid encoding. */
export const decodeRuns = (text: string | null | undefined): ActivityRun[] | null => {
  if (typeof text !== 'string' || !WHOLE_PATTERN.test(text)) return null;
  const runs: ActivityRun[] = [];
  let start = 0;
  RUN_PATTERN.lastIndex = 0;
  let match = RUN_PATTERN.exec(text);
  while (match) {
    const length = Number(match[2]);
    runs.push({ label: match[1], start, length });
    start += length;
    match = RUN_PATTERN.exec(text);
  }
  return runs;
};

export const isAngleLabel = (label: string) => label !== SILENCE_LABEL && label !== SEVERAL_LABEL && label !== NO_SOUND_LABEL;

// ---------------------------------------------------------------------------
// The angles' timing the activity belongs to
// ---------------------------------------------------------------------------

export interface TimedAngle {
  angleKey: string;
  videoId?: string | null;
  timelineStartMs?: number | null;
  sourceInMs?: number | null;
  sourceOutMs?: number | null;
}

const wholeMs = (value: unknown) => Math.max(0, Math.round(Number(value) || 0));

/**
 * One line per angle (key, video, start, trim), in key order. Activity worked
 * out for one basis is wrong for another: changing an angle's video, sync or
 * trim makes it stale. Values are clamped and rounded exactly as the server
 * does, so both sides agree.
 */
export const speakingBasis = (angles: TimedAngle[]): string =>
  angles
    .slice()
    .sort((a, b) => a.angleKey.localeCompare(b.angleKey))
    .map((angle) => [
      angle.angleKey,
      String(angle.videoId ?? ''),
      wholeMs(angle.timelineStartMs),
      wholeMs(angle.sourceInMs),
      angle.sourceOutMs === null || angle.sourceOutMs === undefined ? '' : wholeMs(angle.sourceOutMs),
    ].join(':'))
    .join('|');

/** The activity, when it belongs to these angles as they are now. */
export const currentActivity = (
  activity: SpeakingActivity | null | undefined,
  angles: TimedAngle[]
): SpeakingActivity | null => {
  if (!activity || activity.version !== ACTIVITY_VERSION) return null;
  if (activity.basis !== speakingBasis(angles)) return null;
  return decodeRuns(activity.runs) ? activity : null;
};

// ---------------------------------------------------------------------------
// Who's speaking over a stretch
// ---------------------------------------------------------------------------

/** First run that ends after `frame`. */
const runIndexAt = (runs: ActivityRun[], frame: number): number => {
  let low = 0;
  let high = runs.length - 1;
  let found = runs.length;
  while (low <= high) {
    const middle = (low + high) >> 1;
    if (runs[middle].start + runs[middle].length > frame) {
      found = middle;
      high = middle - 1;
    } else {
      low = middle + 1;
    }
  }
  return found;
};

/** Frames of each label over [startS, endS). */
export const labelFramesBetween = (runs: ActivityRun[], frameS: number, startS: number, endS: number): Record<string, number> => {
  const counts: Record<string, number> = {};
  if (!(frameS > 0) || runs.length === 0) return counts;
  const first = Math.max(0, Math.floor(startS / frameS));
  const end = Math.max(first + 1, Math.ceil(endS / frameS));
  for (let i = runIndexAt(runs, first); i < runs.length && runs[i].start < end; i += 1) {
    const run = runs[i];
    const overlap = Math.min(end, run.start + run.length) - Math.max(first, run.start);
    if (overlap > 0) counts[run.label] = (counts[run.label] ?? 0) + overlap;
  }
  return counts;
};

/**
 * The angle whose person does most of the talking over [startS, endS), or
 * null when it isn't clear: at least half the speech, and twice anyone
 * else's. Silence doesn't count; crosstalk counts against everyone.
 */
export const dominantSpeaker = (runs: ActivityRun[], frameS: number, startS: number, endS: number): string | null => {
  const counts = labelFramesBetween(runs, frameS, startS, endS);
  const labels = Object.keys(counts);
  let speech = counts[SEVERAL_LABEL] ?? 0;
  let best: string | null = null;
  let bestFrames = 0;
  let secondFrames = 0;
  for (let i = 0; i < labels.length; i += 1) {
    const label = labels[i];
    if (!isAngleLabel(label)) continue;
    const frames = counts[label];
    speech += frames;
    if (frames > bestFrames) {
      secondFrames = bestFrames;
      bestFrames = frames;
      best = label;
    } else if (frames > secondFrames) {
      secondFrames = frames;
    }
  }
  // Under 0.3 s of speech is too little to name anyone.
  if (!best || bestFrames * frameS < 0.3 - 1e-9) return null;
  return bestFrames >= 0.5 * speech && bestFrames >= 2 * secondFrames ? best : null;
};

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

export interface NamedAngle {
  angleKey: string;
  label?: string | null;
  speakerName?: string | null;
}

/** The angle's label ("Host 1"), or "Speaker B" when the label only names the camera. */
export const defaultSpeakerName = (angle: NamedAngle): string => {
  const label = String(angle.label ?? '').replace(/\s+/g, ' ').trim();
  const generic = !label || /^angle\s+[a-z]$/i.test(label) || /^master$/i.test(label);
  return generic ? `Speaker ${angle.angleKey}` : label.slice(0, MAX_SPEAKER_NAME_LENGTH);
};

/** The name of the person on this angle, as transcripts show it. */
export const speakerNameOf = (angle: NamedAngle): string => {
  const own = String(angle.speakerName ?? '').replace(/\s+/g, ' ').trim();
  return own ? own.slice(0, MAX_SPEAKER_NAME_LENGTH) : defaultSpeakerName(angle);
};

/** Who's speaking over a stretch of the timeline (seconds), or null when unsure. */
export type SpeakerLookup = (startS: number, endS: number) => string | null;

/**
 * A lookup for transcript lines, or undefined when there's nothing to name
 * people by: no activity, activity for other timing, or activity PithPlay
 * wasn't confident about.
 */
export const createSpeakerLookup = (
  activity: SpeakingActivity | null | undefined,
  angles: Array<NamedAngle & TimedAngle>
): SpeakerLookup | undefined => {
  const usable = currentActivity(activity, angles);
  if (!usable || !usable.confident) return undefined;
  const runs = decodeRuns(usable.runs);
  if (!runs || runs.length === 0) return undefined;
  const frameS = usable.frameMs / 1000;
  const names: Record<string, string> = {};
  angles.forEach((angle) => {
    names[angle.angleKey] = speakerNameOf(angle);
  });
  return (startS, endS) => {
    const key = dominantSpeaker(runs, frameS, startS, endS);
    return key ? names[key] ?? null : null;
  };
};
